import { useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { ChevronRight, Plus } from "lucide-react";
import { THREE_PILLARS } from "@/lib/content";

export default function ThreePillars() {
  const [active, setActive] = useState(0);
  const pillar = THREE_PILLARS[active];

  return (
    <section
      id="three-pillars"
      data-testid="three-pillars"
      className="bg-slate-50 border-y border-slate-200 py-24 md:py-32 relative overflow-hidden"
    >
      <div className="relative mx-auto max-w-[1400px] px-5 md:px-10">
        <div className="flex items-end justify-between flex-wrap gap-8">
          <div>
            <div className="flex items-center gap-3">
              <span className="h-px w-12 bg-accent-blue" />
              <span className="label-eyebrow text-accent-blue">
                The Three Pillars
              </span>
            </div>
            <h2 className="font-heading font-black text-5xl md:text-7xl mt-4 leading-[0.92] tracking-tighter">
              <span className="text-slate-900">Create.</span>
              <br />
              <span className="text-slate-400">Amplify.</span>
              <br />
              <span className="text-accent-blue">Position.</span>
            </h2>
          </div>
          <p className="text-slate-600 text-base md:text-lg max-w-sm leading-relaxed">
            Most marketing agencies create content.
            <br />
            Most publishers distribute content.
            <br />
            <span className="text-slate-900 font-semibold">We do both.</span>
          </p>
        </div>

        {/* Tab strip, light cards */}
        <div className="mt-14 grid grid-cols-3 gap-2 sm:gap-3 md:gap-5">
          {THREE_PILLARS.map((p, i) => {
            const isActive = active === i;
            return (
              <button
                key={p.key}
                type="button"
                data-testid={`three-pillar-tab-${p.key}`}
                onClick={() => setActive(i)}
                onMouseEnter={() => setActive(i)}
                className={`relative text-left p-3 sm:p-5 md:p-7 rounded-2xl transition-all border-2 overflow-hidden min-w-0 ${
                  isActive
                    ? "border-accent-blue bg-white shadow-lg shadow-cyan-500/10"
                    : "border-slate-200 bg-white hover:border-slate-300"
                }`}
              >
                <div className="flex items-center justify-between">
                  <span
                    className={`font-mono text-[10px] sm:text-xs ${
                      isActive ? "text-accent-blue" : "text-slate-400"
                    }`}
                  >
                    {p.code}
                  </span>
                  <ChevronRight
                    className={`h-3.5 w-3.5 sm:h-4 sm:w-4 transition-transform ${
                      isActive
                        ? "text-accent-blue translate-x-0"
                        : "text-slate-300 -translate-x-1"
                    }`}
                  />
                </div>
                <p
                  className={`font-heading font-black text-sm sm:text-2xl md:text-5xl mt-3 sm:mt-4 md:mt-6 tracking-tight leading-tight ${
                    isActive ? "text-slate-900" : "text-slate-500"
                  }`}
                  style={{
                    letterSpacing: "-0.02em",
                    wordBreak: "break-word",
                  }}
                >
                  {p.label}
                </p>
              </button>
            );
          })}
        </div>

        {/* Active pillar detail, dark panel for contrast */}
        <AnimatePresence mode="wait">
          <motion.div
            key={pillar.key}
            initial={{ opacity: 0, y: 24 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -16 }}
            transition={{ duration: 0.4 }}
            className="dark-section mt-8 grid grid-cols-1 lg:grid-cols-12 gap-8 rounded-2xl p-6 md:p-10 lg:p-14 relative"
            style={{ backgroundColor: "#0f172a" }}
          >
            <div className="lg:col-span-5">
              <p className="font-mono text-xs text-accent-blue">
                PILLAR {pillar.code}
              </p>
              <p className="font-heading font-black text-3xl md:text-5xl mt-4 leading-tight tracking-tight text-white">
                {pillar.headline}
              </p>
              <p className="mt-6 text-white/80 text-base md:text-lg leading-relaxed">
                {pillar.intro}
              </p>
            </div>
            <div className="lg:col-span-7">
              <ul
                data-testid={`three-pillar-items-${pillar.key}`}
                className="grid grid-cols-1 sm:grid-cols-2 gap-3"
              >
                {pillar.items.map((it) => (
                  <li
                    key={it}
                    className="bg-white/5 backdrop-blur border border-white/10 rounded-xl p-4 flex items-center gap-3"
                  >
                    <Plus className="h-3.5 w-3.5 text-accent-blue flex-shrink-0" />
                    <span className="text-sm md:text-base font-medium text-white">
                      {it}
                    </span>
                  </li>
                ))}
              </ul>
            </div>
          </motion.div>
        </AnimatePresence>
      </div>
    </section>
  );
}
